import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Banknote } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { payrollApi } from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';
import { ROUTES } from '@/app/router/routes';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { PayrollPeriod } from '@/types/hr';
import { formatDate } from '@/utils/format';
import { Money, PayrollStatusBadge, endOfMonth, startOfMonth } from './shared';

/**
 * Payroll runs.
 *
 * <p>Only one run is open at a time — a partial unique index guarantees it, and
 * the "Open a run" button is hidden while one is in flight rather than left to
 * fail on submit. Two open runs is how the same month gets paid twice.
 */
export function PayrollPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('periodStart,desc');

  const [opening, setOpening] = useState(false);
  const [start, setStart] = useState(startOfMonth());
  const [end, setEnd] = useState(endOfMonth());
  const [payday, setPayday] = useState(endOfMonth());
  const [workingDays, setWorkingDays] = useState('');

  const params = table.params;
  const periods = useQuery({
    queryKey: queryKeys.payroll.list(params),
    queryFn: () => payrollApi.list(params),
  });

  const open = useQuery({
    queryKey: queryKeys.payroll.open,
    queryFn: () => payrollApi.open(),
  });

  const create = useMutation({
    mutationFn: () =>
      payrollApi.openPeriod({
        periodStart: start,
        periodEnd: end,
        paymentDate: payday,
        workingDays: workingDays === '' ? undefined : Number(workingDays),
      }),
    onSuccess: (period) => {
      toast.success(`${period.name} opened`);
      setOpening(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.payroll.all });
      navigate(ROUTES.payrollDetail(period.id));
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<PayrollPeriod>[] = [
    {
      key: 'name',
      header: 'Run',
      sortKey: 'code',
      render: (row) => (
        <span>
          <span className="block text-sm font-medium text-ink">{row.name}</span>
          <span className="block font-mono text-[11px] text-subtle">{row.code}</span>
        </span>
      ),
    },
    {
      key: 'dates',
      header: 'Covers',
      sortKey: 'periodStart',
      render: (row) => (
        <span className="text-sm text-ink">
          {formatDate(row.periodStart)} → {formatDate(row.periodEnd)}
        </span>
      ),
    },
    {
      key: 'payday',
      header: 'Pay date',
      sortKey: 'paymentDate',
      render: (row) => <span className="text-sm text-ink">{formatDate(row.paymentDate)}</span>,
    },
    { key: 'status', header: 'Status', render: (row) => <PayrollStatusBadge status={row.status} /> },
    {
      key: 'employees',
      header: 'Payslips',
      className: 'text-right',
      render: (row) => <span className="tabular-nums text-sm text-ink">{row.employeeCount}</span>,
    },
    {
      key: 'gross',
      header: 'Gross',
      className: 'text-right',
      render: (row) => <Money value={row.totalGross} />,
    },
    {
      key: 'net',
      header: 'Net',
      sortKey: 'totalNet',
      className: 'text-right',
      render: (row) => <Money value={row.totalNet} bold />,
    },
  ];

  const hasOpenRun = (open.data ?? []).length > 0;

  return (
    <>
      <ListPage
        title="Payroll"
        description="One run a month: open it, calculate it, have it approved, then pay it."
        icon={<Banknote className="h-5 w-5" aria-hidden="true" />}
        action={
          can(PERMISSIONS.PAYROLL_PERIOD_MANAGE) && !hasOpenRun ? (
            <Button onClick={() => setOpening(true)}>Open a run</Button>
          ) : undefined
        }
        pagination={periods.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        {hasOpenRun && (
          <div className="border-b border-line bg-surface px-4 py-3">
            <p className="text-[13px] text-muted">
              <span className="font-semibold text-ink">{open.data?.[0]?.name}</span> is still{' '}
              {open.data?.[0]?.status.toLowerCase()}. Finish or cancel it before opening another —
              two runs at once is how a month gets paid twice.
            </p>
          </div>
        )}

        <DataTable
          columns={columns}
          rows={periods.data?.data}
          rowKey={(row) => row.id}
          loading={periods.isPending}
          error={periods.error}
          onRetry={() => void periods.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          onRowClick={(row) => navigate(ROUTES.payrollDetail(row.id))}
          emptyTitle="No payroll runs"
          emptyDescription="Open one for this month to get started."
        />
      </ListPage>

      {opening && (
        <Modal open onClose={() => setOpening(false)} title="Open a payroll run">
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="First day"
                type="date"
                required
                value={start}
                onChange={(event) => setStart(event.target.value)}
              />
              <TextField
                label="Last day"
                type="date"
                required
                min={start}
                value={end}
                onChange={(event) => setEnd(event.target.value)}
              />
            </div>

            <TextField
              label="Payment date"
              type="date"
              required
              min={start}
              value={payday}
              onChange={(event) => setPayday(event.target.value)}
              hint="When people are actually paid, often the following month"
            />

            <TextField
              label="Working days"
              type="number"
              min="1"
              max="31"
              value={workingDays}
              onChange={(event) => setWorkingDays(event.target.value)}
              hint="The divisor pro-rating uses. Blank counts the days in the range; some schools use a fixed 26 or 30"
            />

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={() => setOpening(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Opening…' : 'Open run'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
